// Registre d'events de fitxatge des del portal mòbil (CURRENT_TASK.md
// §10-§26): sempre es desa primer a la cua local (IndexedDB) i després
// s'intenta sincronitzar de seguida. Aquest únic camí -tant si hi ha
// connexió com si no- garanteix que mai es perd un event per una fallada de
// xarxa a mig fitxatge i que l'ordre de sincronització és sempre el mateix
// que el de creació.

import { chFetch, chGenerarUUID, chObtenirUuidTerminal, chNomTerminal } from "./chAuth";
import { chObtenirGeolocalitzacio } from "./chGeo";
import { chGuardarPendent, chLlistarPendents } from "./chIndexedDB";
import { chSincronitzarPendents } from "./chSync";

// "YYYY-MM-DD HH:MM:SS" en hora local (no UTC): és el format que espera
// FitxatgeHelper.php per determinar la Data de jornada.
function formatDataHoraLocal(data) {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${data.getFullYear()}-${pad(data.getMonth() + 1)}-${pad(data.getDate())} ` +
    `${pad(data.getHours())}:${pad(data.getMinutes())}:${pad(data.getSeconds())}`
  );
}

// tipusEvent: 'ENTRADA' | 'INICI_PAUSA' | 'FI_PAUSA' | 'SORTIDA'
// idTipusPausa/nomTipusPausa: obligatori (idTipusPausa) només per a INICI_PAUSA;
// nomTipusPausa és només per poder mostrar-lo mentre l'event és pendent.
//
// Retorna { success: true, queued: bool, uuidEvent }. Si el propi servidor
// rebutja l'event (p. ex. transició d'estat invàlida), llança un Error amb
// el missatge per mostrar-lo a l'usuari; en aquest cas l'event NO es perd:
// queda a la cua local marcat com a "ERROR" fins que es corregeixi.
export async function chRegistrarEvent(tipusEvent, idTipusPausa = null, nomTipusPausa = null) {
  const geo = await chObtenirGeolocalitzacio();
  const ara = new Date();

  const pendent = {
    uuidEvent: chGenerarUUID(),
    tipusEvent,
    dataHoraEvent: formatDataHoraLocal(ara),
    uuidTerminal: chObtenirUuidTerminal(),
    nomTerminal: chNomTerminal(),
    idTipusPausa: idTipusPausa || null,
    nomTipusPausa: nomTipusPausa || null,
    estatGPS: geo.estatGPS,
    latitud: geo.latitud,
    longitud: geo.longitud,
    precisioMetres: geo.precisioMetres,
    estatLocal: "PENDENT",
    ultimError: null,
    dataCreacioLocal: formatDataHoraLocal(ara),
  };

  try {
    await chGuardarPendent(pendent);
  } catch {
    // IndexedDB no disponible (navegador molt antic / mode privat molt
    // restrictiu): es manté un únic intent directe, sense cua, com a
    // degradació mínima en lloc de bloquejar el fitxatge sencer.
    const resposta = await chFetch("/ControlHorariFitxar.php", {
      method: "POST",
      body: JSON.stringify({
        tipusEvent: pendent.tipusEvent,
        uuidEvent: pendent.uuidEvent,
        dataHoraEvent: pendent.dataHoraEvent,
        uuidTerminal: pendent.uuidTerminal,
        nomTerminal: pendent.nomTerminal,
        idTipusPausa: pendent.idTipusPausa,
        esOffline: false,
        estatGPS: pendent.estatGPS,
        latitud: pendent.latitud,
        longitud: pendent.longitud,
        precisioMetres: pendent.precisioMetres,
      }),
    });
    const dades = await resposta.json().catch(() => null);
    if (!dades || !dades.success) {
      throw new Error((dades && dades.message) || "No s'ha pogut registrar l'event.");
    }
    return { success: true, queued: false, uuidEvent: pendent.uuidEvent };
  }

  const resultat = await chSincronitzarPendents();

  if (resultat.motiu === "rebutjat") {
    const pendents = await chLlistarPendents();
    const aquest = pendents.find((p) => p.uuidEvent === pendent.uuidEvent);
    if (aquest && aquest.estatLocal === "ERROR") {
      throw new Error(aquest.ultimError || "No s'ha pogut registrar l'event.");
    }
  }

  const pendentsRestants = await chLlistarPendents();
  const encaraPendent = pendentsRestants.some((p) => p.uuidEvent === pendent.uuidEvent);
  return { success: true, queued: encaraPendent, uuidEvent: pendent.uuidEvent };
}
